import { HelpCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useLocale } from 'hooks/locale';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';

import { checkHistoryActions } from './../../constants/check-history-actions';
import styles from './HistoryRow.module.scss';

export const HistoryRow = ({
  entry,
  candidate,
  companyChecks,
  referees,
  className,
  companyUsers = [],
  company,
  disableLinks = false,
  isList = true,
  onCandidateRequest = () => {},
  onRefereeRequest = () => {},
}) => {
  const { dateSlashFormat } = useLocale(company?.locale);

  // Derived values
  const checkHuman = entry?.check_type
    ? companyChecks?.find(companyCheck => companyCheck.type === entry.check_type)?.check_type.title
    : null;

  const userName = <b className={styles.userRef}>@{entry.user_name || 'User'}&#160;</b>;

  const refCandidate = disableLinks ? (
    <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{candidate.name}</b>
  ) : (
    <Link to={`/check/${candidate.id}/candidate`}>{candidate.name}</Link>
  );

  const backgroundChecks = disableLinks ? null : (
    <Link to={`/check/${candidate.id}/background_checks`}>View</Link>
  );

  const country =
    entry.country && entry.country !== 'Not specified.' ? `from ${entry.country}` : '';
  const reportType = entry.check_type || null;

  const referee = entry.referee_id ? referees.find(r => r.id === entry.referee_id) : null;

  const refReferee = referee ? (
    disableLinks ? (
      <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{referee.name}</b>
    ) : (
      <Link
        to={`/check/${candidate.id}/${
          reportType === 'custom_reference' ? 'reference' : 'referees'
        }/${referee.id}`}>
        {referee.name}
      </Link>
    )
  ) : null;

  const referenceLink =
    referee && !disableLinks ? (
      <Link
        to={`/check/${candidate.id}/${
          reportType === 'custom_reference' ? 'reference' : 'reports'
        }/${referee.id}`}>
        View check
      </Link>
    ) : null;

  // For CompletedCandidate case: joining multiple referee links if needed
  const candidateRefereeLinks =
    referees && referees.length > 0
      ? referees.map((ref, index) => (
          <span key={ref.id}>
            {index > 0 && ' & '}
            {disableLinks ? (
              <b className={cn(styles.userRef, 'u-padding-right--xSmall')}>{ref.name}</b>
            ) : (
              <Link
                to={`/check/${candidate.id}/${
                  reportType === 'custom_reference' ? 'reference' : 'referees'
                }/${ref.id}`}>
                {ref.name}
              </Link>
            )}
          </span>
        ))
      : null;

  // Build the data object to pass into our mapping function
  const actionData = {
    refCandidate,
    refReferee,
    referenceLink,
    candidateRefereeLinks,
    backgroundChecks,
    country,
    styles,
    userName,
    candidate,
    checkHuman,
    entry,
    referee,
    onCandidateRequest,
    onRefereeRequest,
    referees,
    companyUsers,
  };

  // Use the mapping constant to get the action
  const action = checkHistoryActions[entry.type]
    ? checkHistoryActions[entry.type](actionData)
    : { icon: HelpCircleIcon, description: <p>Unknown action.</p> };

  // Map color values to Tailwind classes
  const colorMap = {
    'green-2': 'text-green-2',
    'yellow-2': 'text-yellow-2',
    'blue-2': 'text-blue-2',
    'red-2': 'text-red-2',
    'primary-4': 'text-primary-4',
    'orange-2': 'text-orange-2',
  };
  const iconColorClass = action.color
    ? colorMap[action.color] || 'text-text-primary'
    : 'text-text-primary';

  return (
    <div className="flex flex-row gap-4 items-center bg-surface-primary rounded-3xl p-8 border border-border-primary shadow-soft mb-4">
      <HugeiconsIcon icon={action.icon} size="24" strokeWidth={1.5} className={iconColorClass} />
      <div className="flex flex-row gap-8">
        <span className="text-neutral-7 font-medium text-2xl">
          <Moment format={dateSlashFormat}>{entry.created_at}</Moment>
        </span>
        <span className="text-neutral-7 font-medium text-2xl">
          <Moment format="@HH:mm">{entry.created_at}</Moment>
        </span>
      </div>
      <div className="text-text-primary font-regular text-2xl ml-8">{action.description}</div>
    </div>
  );
};
